import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import type { FilterItemConfig } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getStatementServices } from '@/modules/Service/services'
import {
    createStatementColumns,
    createServiceFilters,
    SERVICE_FILTER_DEFAULTS,
    SERVICE_DEFAULT_SORT,
    STATEMENT_SERVICE_STATUSES,
    type ServiceFilters,
} from '@/modules/Service/utils/service-list-config'

/**
 * Business logic hook for the Statement (reconciliation) service list.
 * Adds a single-date `date` base param (billing cycle) on top of the shared
 * service list filters and wires it into the automation `/services?scope=statement`
 * query.
 */
export const useStatementServiceList = () => {
    const { t } = useTranslation('services')

    const {
        params,
        filters,
        queryParams,
        setParam,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ServiceFilters>({
        defaults: {
            sort_by: SERVICE_DEFAULT_SORT.sort_by,
            sort_order: SERVICE_DEFAULT_SORT.sort_order,
            page_size: 20,
        },
        filterDefaults: SERVICE_FILTER_DEFAULTS,
    })

    // `date` is a top-level query param (not part of the encoded filters blob).
    const [searchParams] = useSearchParams()
    const date = searchParams.get('date') || undefined

    const { control } = useForm<ServiceFilters>({
        defaultValues: filters,
    })

    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_STATEMENT, queryParams, date],
        queryFn: () => getStatementServices({ ...queryParams, date }),
        placeholderData: true,
    })

    const columns = useMemo(() => createStatementColumns(t), [t])

    // Reuse the shared filters (status + product group) and prepend a single
    // billing-cycle date picker mapped to the `date` base param.
    const filterItems = useMemo<FilterItemConfig[]>(() => {
        const dateFilter: FilterItemConfig = {
            name: 'date',
            type: 'daterangepicker',
            names: ['date', 'date_to'],
            label: t('statement-cycle'),
            placeholder: t('select-date-range'),
            numberOfMonths: 1,
            clearable: true,
            onChange: (val) => setParam('date', val?.[0] || undefined),
        }

        return [
            dateFilter,
            ...createServiceFilters(t, {
                serviceStatuses: STATEMENT_SERVICE_STATUSES,
                showAccountManager: false,
                showDateFilters: false,
                showContractFilters: false,
            }),
        ]
    }, [t, setParam])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        services: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        params,
        control,
        setFilter,
        searchInputProps,

        columns,
        filterItems,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        t,
    }
}

export default useStatementServiceList
